/** Back office → Academy: question bank, topics, mock exams, TCF sessions and urgent session exams,
 * grading queue, learners and their progress, trends, sources, settings. D-105: a TCF / IELTS switch
 * at the top filters every tab (`?exam=`); the IELTS adds the course path and the passages. */

import { type ExamEnum } from "@agencyos/api-client";
import {
  AudioLines,
  Banknote,
  BarChart3,
  CalendarClock,
  ClipboardCheck,
  FileStack,
  Gauge,
  GraduationCap,
  Languages,
  ListChecks,
  type LucideIcon,
  Mic,
  PenLine,
  ReceiptText,
  Settings,
  ShieldCheck,
  Star,
  TicketPercent,
  TrendingUp,
  UserPlus,
  UsersRound,
  Video,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../../components/AppShell";
import "./academy-admin.css";
import { CalibrationTab } from "./CalibrationTab";
import { CourseTab } from "./CourseTab";
import { type GoetheLevel, contentLanguage } from "@agencyos/core";

import { EXAMS, ExamProvider, ExamSwitch, GOETHE, IELTS, LevelSwitch, TEF } from "./examContext";
import { GoetheGradingTab } from "./GoetheGrading";
import { TefGradingTab } from "./TefGrading";
import { GradingTab } from "./GradingTab";
import { ReviewRequestsPanel } from "./ReviewRequests";
import { IeltsGradingTab } from "./IeltsGrading";
import { LearnersTab } from "./LearnersTab";
import { LiveTab } from "./LiveTab";
import { MockExamsTab } from "./MockExamsTab";
import { OralTab } from "./OralTab";
import { PassagesTab } from "./PassagesTab";
import { PitfallsReviewTab } from "./PitfallsReviewTab";
import { ProgressTab } from "./ProgressTab";
import { QuestionsTab } from "./QuestionsTab";
import { ReviewsTab } from "./ReviewsTab";
import { PromoCodesTab } from "./sales/PromoCodesTab";
import { SalesTab } from "./sales/SalesTab";
import "./sales/sales-admin.css";
import { SubscriptionsTab } from "./sales/SubscriptionsTab";
import { SessionsTab } from "./SessionsTab";
import { SettingsTab } from "./SettingsTab";
import { useCurrentMembership, useHasPermission, usePosition } from "../../lib/settings";
import { useAcademyRoles } from "./shared";
import { SourcesTab } from "./SourcesTab";
import { StudentsTab } from "./StudentsTab";
import { TopicsTab } from "./TopicsTab";
import { TrendsTab } from "./TrendsTab";

type Tab =
  | "questions"
  | "topics"
  | "course"
  | "passages"
  | "mocks"
  | "sessions"
  | "live"
  | "grading"
  | "calibration"
  | "pitfalls"
  | "students"
  | "learners"
  | "progress"
  | "trends"
  | "sources"
  | "settings"
  | "sales"
  | "subscriptions"
  | "promos"
  | "oral"
  | "reviews";

/** The online sale of the Academy (D-110): the same for every exam. */
const SALES_TABS: Tab[] = ["sales", "subscriptions", "promos"];

/** D-115: the tabs each role may open (the API refuses the others, see api_sales.py). The
 * accountant reads the sales only; the advisor never the sales; D-120: the trainer no sales data at
 * all; the « Students » tab follows the permission of the position (academy.students.read). */
function allowedByRole(role: string | undefined, key: Tab, students = false): boolean {
  if (key === "students") return students;
  // D-142: the mock orals (the accountant reads their statistics) and the reviews
  if (role === "accountant") return SALES_TABS.includes(key) || key === "oral";
  if (role === "advisor") return !SALES_TABS.includes(key) && key !== "oral" && key !== "reviews";
  if (role === "trainer") return !SALES_TABS.includes(key);
  return true;
}

/** Sections of the IELTS only (the shared passages); the course path serves both exams (D-107). */
const IELTS_ONLY: Tab[] = ["passages"];
/** Sections whose name changes with the exam (« Questions Listening / Reading »…). */
const IELTS_NAMES: Tab[] = ["questions", "topics", "sessions", "trends"];

/** Design v2.1: the twelve sections grouped in a side column (like System Settings), with icons. */
const GROUPS: { key: string; tabs: Tab[] }[] = [
  { key: "content", tabs: ["course", "passages", "questions", "topics", "mocks", "sessions"] },
  { key: "teaching", tabs: ["live", "oral", "grading", "calibration", "pitfalls"] },
  { key: "learners", tabs: ["students", "learners", "progress", "reviews"] },
  { key: "watch", tabs: ["trends", "sources"] },
  { key: "sales", tabs: ["sales", "subscriptions", "promos"] },
  { key: "admin", tabs: ["settings"] },
];
const ICONS: Record<Tab, LucideIcon> = {
  questions: ListChecks,
  topics: PenLine,
  course: GraduationCap,
  passages: AudioLines,
  mocks: FileStack,
  sessions: CalendarClock,
  live: Video,
  grading: ClipboardCheck,
  calibration: Gauge,
  pitfalls: Languages,
  students: UserPlus,
  learners: UsersRound,
  progress: TrendingUp,
  trends: BarChart3,
  sources: ShieldCheck,
  settings: Settings,
  sales: Banknote,
  subscriptions: ReceiptText,
  promos: TicketPercent,
  oral: Mic,
  reviews: Star,
};

export function AcademyAdminPage() {
  const { t } = useTranslation();
  const { canEdit, role } = useAcademyRoles();
  const canReadStudents = useHasPermission("academy.students.read");
  const position = usePosition();
  const [picked, setTab] = useState<Tab | null>(null);
  // D-120: a trainer sees the exams of the languages they teach only (none chosen: all of them)
  const taught = useCurrentMembership()?.languages ?? [];
  const exams = EXAMS.filter((code) => !taught.length || taught.includes(contentLanguage(code)));
  const [chosenExam, setExam] = useState<ExamEnum | null>(null);
  const exam: ExamEnum = chosenExam && exams.includes(chosenExam) ? chosenExam : (exams[0] ?? "tcf_canada");
  const [level, setLevel] = useState<GoetheLevel>("B1");
  const ielts = exam === IELTS;
  const tef = exam === TEF; // D-109: documents of several questions, like the IELTS
  const goethe = exam === GOETHE; // D-118: German, one exam per level
  const visible = (key: Tab) => allowedByRole(role, key, canReadStudents) && (ielts || tef || goethe || !IELTS_ONLY.includes(key));
  // The accountant starts on the sales (the question bank is not theirs).
  // D-120: the customer support starts on the students (their daily work).
  const tab: Tab = picked ?? (role === "accountant" ? "sales" : position === "support" ? "students" : "questions");
  // D-115: no tab is opened before the role is known (the question bank was requested, then
  // refused with a 403, while the accountant's role was still loading)
  const ready = role !== undefined;
  const label = (key: Tab) =>
    goethe && (IELTS_ONLY.includes(key) || IELTS_NAMES.includes(key))
      ? t(`goethe.admin.tab.${key}`)
      : tef && (IELTS_ONLY.includes(key) || IELTS_NAMES.includes(key))
      ? t(`tef.admin.tab.${key}`)
      : ielts && (IELTS_ONLY.includes(key) || IELTS_NAMES.includes(key))
      ? t(`academyAdmin.ielts.tab.${key}`)
      : key === "live"
        ? t("academyLive.admin.tab")
        : key === "oral"
          ? t("coaching.admin.tab")
        : key === "reviews"
          ? t("coaching.admin.reviewsTab")
        : key === "calibration"
          ? t("academyCalibration.tab")
          : key === "pitfalls"
            ? t("pitfallsReview.tab")
          : key === "course"
            ? t("academyAdmin.ielts.tab.course")
            : key === "students"
              ? t("access.students.tab")
            : SALES_TABS.includes(key)
              ? t(`academySales.admin.tab.${key}`)
              : t(`academyAdmin.tab.${key}`);
  const onExam = (next: ExamEnum) => {
    setExam(next);
    if (next !== IELTS && next !== TEF && next !== GOETHE && IELTS_ONLY.includes(tab)) setTab("questions");
  };
  return (
    <Page title={t("academyAdmin.title")} description={t("academyAdmin.intro")} actions={
        <>
          <ExamSwitch value={exam} onChange={onExam} exams={exams} />
          {goethe && <LevelSwitch value={level} onChange={setLevel} />}
        </>
      }
    >
      <ExamProvider exam={exam} level={level}>
      {role !== undefined && !canEdit ? (
        <p role="note" className="muted">
          {t("academyAdmin.readOnly")}
        </p>
      ) : null}
      <div className="academy-admin">
      <nav className="academy-admin__nav ao-glass" aria-label={t("academyAdmin.sections")}>
        {GROUPS.filter((group) => group.tabs.some(visible)).map((group) => (
          <div key={group.key} className="academy-admin__group">
            <p className="academy-admin__group-title" id={`academy-group-${group.key}`}>
              {group.key === "sales" ? t("academySales.admin.group") : t(`academyAdmin.groups.${group.key}`)}
            </p>
            <div role="tablist" aria-orientation="vertical" aria-labelledby={`academy-group-${group.key}`}>
              {group.tabs.filter(visible).map((key) => {
                const Icon = ICONS[key];
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    id={`academy-tab-${key}`}
                    aria-selected={tab === key}
                    aria-controls={`academy-panel-${key}`}
                    className={`academy-admin__tab${tab === key ? " active" : ""}`}
                    onClick={() => setTab(key)}
                  >
                    <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
                    <span>{label(key)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div role="tabpanel" key={`${exam}-${goethe ? level : ""}-${tab}`} className="academy-admin__panel" id={`academy-panel-${tab}`} aria-labelledby={`academy-tab-${tab}`}>
        {ready ? (
          <>
        {tab === "questions" ? <QuestionsTab /> : null}
        {tab === "topics" ? <TopicsTab /> : null}
        {tab === "course" ? <CourseTab /> : null}
        {tab === "passages" && (ielts || tef || goethe) ? <PassagesTab /> : null}
        {tab === "mocks" ? <MockExamsTab /> : null}
        {tab === "sessions" ? <SessionsTab onOpenMocks={() => setTab("mocks")} /> : null}
        {tab === "live" ? <LiveTab /> : null}
        {tab === "oral" ? <OralTab /> : null}
        {tab === "reviews" ? <ReviewsTab /> : null}
        {tab === "grading" ? <ReviewRequestsPanel /> : null}
        {tab === "grading" ? goethe ? <GoetheGradingTab /> : ielts ? <IeltsGradingTab /> : tef ? <TefGradingTab /> : <GradingTab /> : null}
        {tab === "calibration" ? <CalibrationTab /> : null}
        {tab === "pitfalls" ? <PitfallsReviewTab /> : null}
        {tab === "students" ? <StudentsTab /> : null}
        {tab === "learners" ? <LearnersTab /> : null}
        {tab === "progress" ? <ProgressTab /> : null}
        {tab === "trends" ? <TrendsTab /> : null}
        {tab === "sources" ? <SourcesTab /> : null}
        {tab === "settings" ? <SettingsTab /> : null}
        {tab === "sales" ? <SalesTab /> : null}
        {tab === "subscriptions" ? <SubscriptionsTab /> : null}
        {tab === "promos" ? <PromoCodesTab /> : null}
          </>
        ) : null}
      </div>
      </div>
      </ExamProvider>
    </Page>
  );
}
